import { Link } from 'react-router-dom';
import { Card, Badge, Button } from '@rackd/ui';
import {
  IconStar,
  IconStarFilled,
  IconCalendar,
  IconBarbell,
  IconCrown,
  IconPencil,
} from '@tabler/icons-react';
import type { CatalogProgram } from '../api';
import { useToggleFeatured } from '../hooks/useCatalog';

interface CatalogCardProps {
  program: CatalogProgram;
}

const levelColors = {
  beginner: 'bg-[var(--success)]/10 text-[var(--success)]',
  intermediate: 'bg-[var(--warning)]/10 text-[var(--warning)]',
  advanced: 'bg-[var(--error)]/10 text-[var(--error)]',
};

const tierColors = {
  free: 'bg-[var(--text-tertiary)]/10 text-[var(--text-tertiary)]',
  power_rack: 'bg-[var(--pr-gold)]/10 text-[var(--pr-gold)]',
};

export function CatalogCard({ program }: CatalogCardProps) {
  const { toggleFeatured, isToggling } = useToggleFeatured();

  const handleToggleFeatured = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await toggleFeatured(program.id, !program.isFeatured);
  };

  return (
    <Card className="p-4 hover:border-[var(--accent-primary)]/30 transition-colors">
      <div className="flex items-start gap-4">
        {/* Image or placeholder */}
        <div className="w-20 h-20 bg-[var(--background-tertiary)] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
          {program.imageUrl ? (
            <img
              src={program.imageUrl}
              alt={program.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <IconBarbell size={32} className="text-[var(--text-tertiary)]" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-[var(--text)] truncate">
                  {program.name}
                </h3>
                {program.isFeatured && (
                  <IconStarFilled size={16} className="text-[var(--pr-gold)]" />
                )}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className={levelColors[program.level]}>{program.level}</Badge>
                <Badge className={tierColors[program.accessTier]}>
                  {program.accessTier === 'power_rack' && <IconCrown size={12} />}
                  {program.accessTier === 'power_rack' ? 'Power Rack' : 'Free'}
                </Badge>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleFeatured}
                disabled={isToggling}
                className={`p-2 rounded-lg transition-colors ${
                  program.isFeatured
                    ? 'bg-[var(--pr-gold)]/10 text-[var(--pr-gold)]'
                    : 'hover:bg-[var(--background-tertiary)] text-[var(--text-tertiary)]'
                }`}
                title={program.isFeatured ? 'Remove from featured' : 'Add to featured'}
              >
                {program.isFeatured ? <IconStarFilled size={18} /> : <IconStar size={18} />}
              </button>
              <Link to={`/catalog/${program.id}/edit`}>
                <Button variant="ghost" size="sm">
                  <IconPencil size={16} />
                </Button>
              </Link>
            </div>
          </div>

          {/* Description */}
          {program.description && (
            <p className="text-sm text-[var(--text-tertiary)] line-clamp-2 mb-2">
              {program.description}
            </p>
          )}

          {/* Meta */}
          <div className="flex items-center gap-4 text-xs text-[var(--text-tertiary)]">
            <span className="flex items-center gap-1">
              <IconCalendar size={14} />
              {program.durationWeeks} weeks
            </span>
            <span className="flex items-center gap-1">
              <IconBarbell size={14} />
              {program.daysPerWeek} days/week
            </span>
            <span className="px-2 py-0.5 bg-[var(--background-tertiary)] rounded text-xs">
              {program.focus}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
